import React, { FC, useState, useCallback } from "react";
import { Box, CircularProgress, Chip, Menu, MenuItem } from "@mui/material";
import { observer } from "mobx-react";

import { CorrectBillInfoProps } from "../types/CorrectBill.types";
import { DeleteConfirmationDialog } from "../../../DirectBilling/common/exports";
import { WaitingBackdrop } from "../../../../Base/components/BackDrop";
import useCorrectionBillInfo from "../hooks/useCorrectionBillInfo";
import { CorrectionHeaderActions } from "./sub-components/CorrectionHeaderActions";
import CorrectionBillCardNew from "./sub-components/CorrectionBillCardNew";
import TransactionSpotlightSearch from "./sub-components/TransactionSpotlightSearch";

const CorrectionBillInfo: FC<CorrectBillInfoProps> = observer((props) => {
  const {
    // state
    openDeleteDialog,
    tableVisible,
    isLoading,
    correctionBills,
    notes,
    isSaving,

    // handlers
    setSelectedRxId,
    handleSave,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleNotesChange,
    handleDeleteClick,
    handleRemoveAll,
    handleRxsFromTransactionSelected,

    // from props / from hook
    onRowClick,
    selectedRxId,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
    account,
    userName,
  } = useCorrectionBillInfo(props);

  const [isSpotlightOpen, setIsSpotlightOpen] = useState(false);

  // State for stepper
  const [activeStep, setActiveStep] = useState(0);

  // Compute stepper logic based on the state of the correction bills
  const computeStep = useCallback(() => {
    if (correctionBills.length > 0) {
      // If we have bills loaded, we're at least at step 0
      if (isSaving) {
        return 1; // When saving, we want to show step 1
      } else {
        return 0; // Otherwise we're at step 0 - bills loaded but not processed
      }
    }
    return 0; // No bills loaded
  }, [correctionBills.length, isSaving]);

  // Update step when relevant state changes
  React.useEffect(() => {
    setActiveStep(computeStep());
  }, [computeStep]);

  const handleSaveClicked = async () => {
    // The activeStep will be updated via the effect when isSaving changes to true
    await handleSave();
    // The stepper will automatically update and then the component will transition to EditCorrectionBillFullScreen
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <WaitingBackdrop
        isWorking={isSaving}
        message="Bitte warten"
      />
      {/* Header Buttons */}
      <Box
        sx={{
          padding: 1,
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "#5c758a",
          borderRadius: "0px 0px 10px 10px",
        }}
      >
        {/* Header Buttons - Using CorrectionHeaderActions without "Alle laden" */}
        <CorrectionHeaderActions
          activeStep={activeStep}
          tableVisible={tableVisible}
          onAddRx={() => setIsSpotlightOpen(true)}
          onFilterValid={() => {}}
          onSaveBills={handleSaveClicked}
        />
      </Box>

      {/* Search Transaction Number Modal */}
      <TransactionSpotlightSearch
        accountId={account}
        onRxsSelected={(rxPreviews) => {
          handleRxsFromTransactionSelected(rxPreviews);
          setIsSpotlightOpen(false);
        }}
        isLoading={isLoading}
        setIsLoading={(loading) => {}}
        isOpen={isSpotlightOpen}
        onClose={() => setIsSpotlightOpen(false)}
      />

      {/* Loading */}
      {isLoading && !isSpotlightOpen && (
        <Box className="loading-container">
          <CircularProgress color="primary" />
          <Chip
            label="Daten werden geladen, bitte warte..."
            className="loading-chip"
          />
        </Box>
      )}

      {tableVisible &&
        correctionBills.length > 0 &&
        correctionBills.map((correctionBill) => (
          <Box
            className="bill-card"
            key={correctionBill.payer.ik}
          >
            <CorrectionBillCardNew
              bill={correctionBill}
              account={account}
              userName={userName}
              notes={notes[correctionBill.payer.ik] || ""}
              onNotesChange={(val: string) => handleNotesChange(correctionBill.payer.ik, val)}
              selectedRxId={selectedRxId}
              onRowClick={(row: any) => {
                onRowClick(row);
                setSelectedRxId(row.id);
              }}
              onRemoveAll={handleRemoveAll}
              onMenuOpen={handleMenuOpen}
            />
          </Box>
        ))}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleDeleteClick}>Entfernen</MenuItem>
      </Menu>
    </Box>
  );
});

export default CorrectionBillInfo;
